import { FormTitle } from "@components/atoms/FormTitle/FormTitle";
import { useRef, ChangeEvent, useEffect } from "react";
import { useWasteStore } from "../../../../store/store";
import { sasTokenQuery } from "../wasteUtils/helpers/sasToken";
import { NavigationButtons } from "@components/molecules/NavigationButtons/NavigationButtons";
import { useNavigate } from "react-router-dom";
import { RoutesList } from "../../../../routes/Routes";
import upload from "../../../../assets/images/upload.svg";
import { useMutation } from "@tanstack/react-query";
import { InlineErrorMessage } from "@components/atoms/Errors/InlineErrorMessage";
import { usePhotoInferredResponse } from "@hooks/usePhotoInferredResponse";
import Resizer from "react-image-file-resizer";
import * as Sentry from "@sentry/react";

const returnFileSize = (fileSize: number) => {
  if (fileSize < 1024) {
    return `${fileSize} bytes`;
  } else if (fileSize > 1024 && fileSize < 1048576) {
    return (fileSize / 1024).toFixed(1) + "KB";
  } else if (fileSize > 1048576) {
    return (fileSize / 1048576).toFixed(1) + "MB";
  }
  return "";
};

const isInvalidImageFileType = (file: File | undefined | null) => {
  if (!file) {
    return true;
  }
  const validImageTypes = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
  ];
  return validImageTypes.filter((type) => type === file.type).length === 0;
};

export const UploadPhoto = () => {
  const navigate = useNavigate();
  const data = sasTokenQuery();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const networkError = `An error has occurred. Please try again.`;
  const { mutate: mutatePhotoData, isLoading: loadingInferImage } =
    usePhotoInferredResponse();

  const {
    imageUrl,
    selectedImage,
    blobEndpoint,
    imageUploadError,
    updateImageUrl,
    updateSelectedImage,
    updateBlobUri,
    updateBlobEndpoint,
    updateImageUploadError,
    updateIsImageMismatch,
    updateFilename,
    wasteCategory,
  } = useWasteStore();

  const {
    mutate,
    isLoading: loadingToBlob,
    isError,
  } = useMutation({
    mutationFn: async (selectedImage: File) => {
      const method = "PUT";
      return await fetch(data.data!.blob_endpoint, {
        method,
        headers: { "x-ms-blob-type": "BlockBlob" },
        body: selectedImage,
      })
        .then((response) => {
          updateBlobEndpoint(response?.url);
          updateBlobUri(data.data?.blob_uri);
          const image_uri = data.data?.blob_uri;
          const filename = selectedImage?.name;
          if (image_uri && filename) {
            mutatePhotoData({
              inferImagePayload: {
                image_uri,
                filename,
              },
              userCategory: wasteCategory,
            });
          }
          return response;
        })
        .catch((error) => {
          Sentry.captureException(error);
          throw new Error((error as Error).message);
        });
    },
  });

  const handleInputClick = () => {
    if (fileInputRef.current !== null) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateFilename(event.target.files![0].name);
    const imageFile = event.target.files?.[0];
    const selectedFile = imageFile as File;

    if (selectedFile !== null) {
      const maxSizeInBytes = 1024 * 1024 * 30;

      if (selectedFile.size >= maxSizeInBytes) {
        const fileSize = returnFileSize(selectedFile.size);
        const maxFileSize = returnFileSize(maxSizeInBytes);
        const imageUploadError = `The file you have uploaded is ${fileSize}, the file must be less than ${maxFileSize}`;
        updateImageUploadError(imageUploadError);
      } else if (isInvalidImageFileType(selectedFile)) {
        const imageUploadError = `The file you have uploaded is not a valid image file type, we only accept JPEG, PNG, GIF, WebP file types.`;
        updateImageUploadError(imageUploadError);
      } else {
        updateImageUploadError("");
        updateSelectedImage(selectedFile);
      }
    }
  };

  const resizeFile = async (file: File) =>
    await new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        300,
        300,
        "JPEG",
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        "base64",
      );
    });

  useEffect(() => {
    if (selectedImage !== null) {
      const uploadNow = async () => {
        const file = selectedImage;
        const image = (await resizeFile(file)) as string | ArrayBuffer;
        updateImageUrl(image);
      };
      uploadNow().catch((error) => {
        Sentry.captureException(error);
        updateImageUploadError("Please refresh the page and try again");
      });
    }
  }, [selectedImage]);

  const handleUploadToBlob = async () => {
    updateIsImageMismatch(false);
    if (selectedImage === null) {
      updateImageUploadError("");
      return navigate(RoutesList.WASTE_DETAILS);
    }
    mutate(selectedImage);
  };

  const removePhoto = async () => {
    try {
      if (blobEndpoint.length > 1) {
        await handleDeleteFromBlob();
      } else if (fileInputRef.current !== null) {
        updateImageUrl("");
        updateSelectedImage(null);
        fileInputRef.current.value = "";
      }
    } catch (error) {
      updateImageUploadError(networkError);
    }
  };

  const { mutate: deleteMutate } = useMutation({
    mutationFn: async (blobEndpoint: string) => {
      const method = "DELETE";
      return await fetch(blobEndpoint, {
        method,
        headers: { "x-ms-blob-type": "BlockBlob" },
      }).then(() => {
        updateImageUrl("");
        updateSelectedImage(null);
        updateBlobEndpoint("");
        updateBlobUri("");
      });
    },
  });

  const handleDeleteFromBlob = async () => {
    if (blobEndpoint === null) return;
    deleteMutate(blobEndpoint);
  };

  return (
    <main className="flex flex-col gap-y-4">
      <FormTitle text="1. Upload photo" />
      <section>
        <p className="mt-5">
          {`If you upload a photo, we'll be able to predict your problem, and the location of the issue.`}
        </p>

        <p className="mt-5">
          We accept the following types of images: JPEG, PNG, GIF, WebP. File
          size must be less than 30MB.
        </p>
        <p className="mt-5">
          Find out{" "}
          <a
            className="text-link-colour underline visited:text-link-active-colour hover:text-link-hover-colour"
            href="https://www.westminster.gov.uk/data-protection/report-it-image-disclaimer"
            target="_blank"
            rel="noopener noreferrer"
          >
            how we will be using your image
          </a>
          .
        </p>
      </section>

      <section>
        <InlineErrorMessage message={imageUploadError} />
        {isError && <InlineErrorMessage message={networkError} />}
        <button
          className="mx-auto my-6 flex h-64 w-full items-center justify-between gap-x-5 rounded-xl border-2 border-dashed border-gray-400 sm:ml-0 sm:flex-grow md:h-[280px] md:w-[631px] lg:h-[300px] lg:w-[755px]"
          onClick={handleInputClick}
          style={{ display: imageUrl !== "" ? "none" : "flex" }}
        >
          <span className="upload-font mx-auto my-6 flex flex-col items-center justify-between gap-x-5">
            <img src={upload} alt="Upload photo" className="p-2" />
            Upload photo
          </span>
        </button>
        <input
          alt="Waste photo"
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".jpeg, .png, .jpg, .gif, .webP"
          onChange={handleFileChange}
        />
      </section>

      {imageUrl && typeof imageUrl === "string" && (
        <div className="flex flex-col">
          <img
            className="mx-auto my-5 flex h-[200px] w-[124px] items-center justify-between gap-x-5 rounded-xl bg-gray-200 sm:ml-0 sm:flex-grow md:h-[300px] md:w-[231px] lg:h-[400px] lg:w-[308px]"
            src={imageUrl}
            alt="Selected image"
          />
          <button
            className="upload-font mx-auto flex items-center justify-center underline sm:ml-0"
            onClick={removePhoto}
          >
            Remove photo
          </button>
        </div>
      )}

      <NavigationButtons
        next={handleUploadToBlob}
        isLoading={loadingToBlob || loadingInferImage}
        previous={() => {
          updateImageUploadError("");
          navigate(RoutesList.WASTE_HOME);
        }}
        primaryButtonText={imageUrl !== "" ? "Next" : "I do not have a photo"}
        secondaryButtonText="Previous"
      />
    </main>
  );
};
